import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, Search, X, UserRound, Sparkles } from "lucide-react";
import { useReader } from "../../context/ReaderContext";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "../ui/sheet";
import { Logo, BrandIcon } from "./Logo";
import { ThemeToggle } from "./ThemeToggle";
import { useCategories } from "../../lib/hooks";
import { fmtToday } from "../../lib/api";
import { ScriptToggle } from "../../lib/script";

const SearchForm = ({ onDone, id = "header", autoFocus }) => {
  const [q, setQ] = useState("");
  const nav = useNavigate();
  const submit = (e) => {
    e.preventDefault();
    if (q.trim().length < 2) return;
    nav(`/pretraga?q=${encodeURIComponent(q.trim())}`);
    setQ("");
    onDone?.();
  };
  return (
    <form role="search" onSubmit={submit} className="relative w-full" data-testid={`${id}-search-form`}>
      <label htmlFor={`${id}-search`} className="sr-only">Pretraga</label>
      <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
      <input id={`${id}-search`} autoFocus={autoFocus} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Pretražite vijesti i vodiče"
        className="h-11 w-full rounded-sm border border-input bg-background pl-10 pr-3 text-[14px] outline-none transition-shadow focus:border-ink focus:ring-1 focus:ring-ink" data-testid={`${id}-search-input`} />
    </form>
  );
};

const ReaderLink = ({ className = "", testId }) => {
  const { reader } = useReader();
  return (
    <Link to={reader ? "/moje-vijesti" : "/moj-balkan-info"} className={`inline-flex items-center gap-1.5 font-bold ${className}`} data-testid={testId}>
      <Sparkles className="h-3.5 w-3.5" /> {reader ? "Moje vijesti" : "Moj Balkan Info"}
    </Link>
  );
};

const AccountLink = ({ className = "", testId }) => {
  const { reader } = useReader();
  return <Link to={reader ? "/moj-balkan-info" : "/prijava"} className={className} data-testid={testId}>{reader ? "Moj račun" : "Prijava"}</Link>;
};

const TopStrip = () => (
  <div className="hidden bg-navy text-[12px] text-white/80 lg:block" data-testid="top-info-bar">
    <div className="container-news flex h-8 items-center justify-between">
      <span data-testid="top-bar-date">{fmtToday()}</span>
      <div className="flex items-center gap-5">
        <nav aria-label="Pomoćna navigacija" className="flex items-center gap-5">
          {[["najnovije", "Najnovije"], ["vodici", "Vodiči"], ["o-nama", "O nama"], ["kontakt", "Kontakt"]].map(([s, l]) => (
            <Link key={s} to={`/${s}`} className="transition-colors hover:text-white" data-testid={`topbar-link-${s}`}>{l}</Link>
          ))}
        </nav>
        <span className="h-3.5 w-px bg-white/25" aria-hidden />
        <ReaderLink className="text-pulse-light hover:text-white" testId="topbar-moj-balkan-link" />
        <Link to="/pitaj-balkan-ai" className="font-bold text-pulse-light hover:text-white" data-testid="topbar-pitaj-ai-link">Pitaj Balkan AI</Link>
        <Link to="/alati" className="font-bold text-pulse-light hover:text-white" data-testid="topbar-alati-link">Alati</Link>
        <Link to="/formulari" className="font-bold text-pulse-light hover:text-white" data-testid="topbar-formulari-link">Formulari</Link>
        <AccountLink className="transition-colors hover:text-white" testId="topbar-account-link" />
        <ScriptToggle className="text-white" />
        <ThemeToggle className="h-7 w-7 text-white" />
        <Link to="/admin" className="inline-flex items-center gap-1.5 transition-colors hover:text-white" data-testid="topbar-admin-link"><UserRound className="h-3.5 w-3.5" /> Redakcija</Link>
      </div>
    </div>
  </div>
);

const navCls = ({ isActive }) =>
  `relative whitespace-nowrap py-3.5 text-[13px] font-bold tracking-[-0.005em] transition-colors hover:text-pulse xl:text-[13.5px] after:absolute after:inset-x-0 after:bottom-0 after:h-[3px] after:transition-colors ${isActive ? "text-ink after:bg-pulse" : "text-foreground/80 after:bg-transparent"}`;

const MobileMenu = ({ cats }) => {
  const [open, setOpen] = useState(false);
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button aria-label="Otvori meni" className="-ml-2 inline-flex h-11 w-11 items-center justify-center rounded-sm hover:bg-surface lg:hidden" data-testid="mobile-menu-button">
          <Menu className="h-[22px] w-[22px]" />
        </button>
      </SheetTrigger>
      <SheetContent side="left" className="w-[88vw] max-w-sm overflow-y-auto bg-background p-0" data-testid="mobile-nav-drawer">
        <SheetTitle className="sr-only">Navigacija</SheetTitle>
        <div className="flex items-center border-b border-rule px-5 py-4"><Logo className="h-9" testId="mobile-drawer-logo" /></div>
        <div className="px-5 pt-4"><SearchForm id="drawer" onDone={() => setOpen(false)} /></div>
        <div className="mx-5 mt-4 flex items-center justify-between rounded-sm border border-pulse/40 bg-pulse/[0.06] px-4 py-3 text-[14px]" onClick={() => setOpen(false)}>
          <ReaderLink className="text-pulse" testId="mobile-moj-balkan-link" />
          <Link to="/pitaj-balkan-ai" className="font-bold text-pulse" data-testid="mobile-pitaj-ai-link">Pitaj Balkan AI</Link>
          <Link to="/alati" className="font-bold text-pulse" data-testid="mobile-alati-link">Alati</Link>
          <Link to="/formulari" className="font-bold text-pulse" data-testid="mobile-formulari-link">Formulari</Link>
          <AccountLink className="font-semibold underline" testId="mobile-account-link" />
        </div>
        <nav aria-label="Glavna navigacija" className="px-5 pb-8 pt-2">
          <ul className="divide-y divide-rule">
            {[{ slug: "vijesti", name: "Sve vijesti" }, { slug: "najnovije", name: "Najnovije" }, ...cats].map((c) => (
              <li key={c.slug}><NavLink to={`/${c.slug}`} onClick={() => setOpen(false)} className="flex min-h-[48px] items-center font-display text-[1.08rem] font-semibold text-ink" data-testid={`mobile-nav-${c.slug}`}>{c.name}</NavLink></li>
            ))}
          </ul>
          <div className="mt-6 grid grid-cols-2 gap-3 text-[14px] text-muted-foreground">
            {[["o-nama", "O nama"], ["kontakt", "Kontakt"], ["partneri", "Partneri"], ["urednicka-politika", "Urednička politika"], ["admin", "Redakcija"]].map(([s, l]) => (
              <Link key={s} to={`/${s}`} onClick={() => setOpen(false)} className="py-1" data-testid={`mobile-nav-${s}`}>{l}</Link>
            ))}
          </div>
          <div className="mt-6 flex items-center gap-4 rounded-sm bg-navy px-4 py-3 text-white">
            <span className="text-[12px] font-semibold">Pismo</span><ScriptToggle className="text-white" />
            <span className="ml-auto text-[12px] font-semibold">Tema</span><ThemeToggle className="text-white" />
          </div>
        </nav>
      </SheetContent>
    </Sheet>
  );
};

export const SiteHeader = () => {
  const cats = useCategories();
  const [searchOpen, setSearchOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 150);
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);
  return (
    <>
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:bg-card focus:p-3">Preskoči na sadržaj</a>
      <header data-testid="site-header">
        <TopStrip />
        {/* mobile / tablet single row */}
        <div className="sticky top-0 z-40 border-b border-rule bg-background/95 backdrop-blur lg:hidden" data-testid="mobile-header">
          <div className="container-news flex h-14 items-center justify-between gap-2">
            <div className="flex min-w-0 items-center gap-1.5">
              <MobileMenu cats={cats} />
              <Link to="/" aria-label="BalkanPuls.de početna" className="flex items-center" data-testid="header-logo-link">
                <Logo className="h-[30px] min-[400px]:h-[34px] sm:h-[38px]" eager testId="header-logo-mobile" />
              </Link>
            </div>
            <div className="flex items-center">
              <Link to="/moj-balkan-info" aria-label="Moj Balkan Info" className="inline-flex h-11 w-11 items-center justify-center rounded-sm text-pulse hover:bg-surface" data-testid="mobile-moj-balkan-icon"><Sparkles className="h-5 w-5" /></Link>
              <button aria-label={searchOpen ? "Zatvori pretragu" : "Pretraga"} onClick={() => setSearchOpen((v) => !v)} className="inline-flex h-11 w-11 items-center justify-center rounded-sm hover:bg-surface" data-testid="mobile-search-toggle">
                {searchOpen ? <X className="h-5 w-5" /> : <Search className="h-5 w-5" />}
              </button>
              <ThemeToggle className="h-11 w-11 hover:bg-surface" />
            </div>
          </div>
          {searchOpen && <div className="container-news pb-3"><SearchForm id="mobile" autoFocus onDone={() => setSearchOpen(false)} /></div>}
        </div>
        {/* desktop brand row */}
        <div className="hidden border-b border-rule lg:block">
          <div className="container-news flex items-center justify-between gap-10 py-6">
            <Link to="/" aria-label="BalkanPuls.de početna" data-testid="header-logo-link-desktop"><Logo className="h-[64px] xl:h-[72px]" eager testId="header-logo" /></Link>
            <div className="w-[360px]"><SearchForm /></div>
          </div>
        </div>
      </header>
      <nav aria-label="Kategorije" className={`sticky top-0 z-30 hidden border-b border-rule bg-background/95 backdrop-blur transition-shadow lg:block ${scrolled ? "shadow-[0_1px_0_hsl(var(--rule)),0_8px_24px_-18px_rgba(11,30,60,.35)]" : ""}`} data-testid="main-category-nav">
        <div className="container-news flex items-center gap-[1.05rem] xl:gap-6">
          <Link to="/" aria-label="Početna" tabIndex={scrolled ? 0 : -1} className={`-mr-2 shrink-0 transition-all duration-300 ${scrolled ? "w-8 opacity-100" : "w-0 opacity-0"}`} data-testid="nav-sticky-icon">
            <BrandIcon className="h-8 w-8" testId="nav-icon" />
          </Link>
          <NavLink to="/vijesti" className={navCls} data-testid="category-nav-vijesti">Vijesti</NavLink>
          <NavLink to="/najnovije" className={navCls} data-testid="category-nav-najnovije">Najnovije</NavLink>
          <span className="h-4 w-px bg-rule" aria-hidden />
          {cats.map((c) => (
            <NavLink key={c.slug} to={`/${c.slug}`} className={navCls} data-testid={`category-nav-${c.slug}`}>{c.name}</NavLink>
          ))}
        </div>
      </nav>
    </>
  );
};
